import { useEffect } from 'react'

import { canReloadNow, markReloaded } from '@/utils'

// Note: the exact wording browser engines use for a failed dynamic `import()` (e.g. after
// a deploy deleted the hashed chunk file a stale tab still references) — Chrome/Edge,
// Firefox, and Safari each phrase it differently.
const CHUNK_LOAD_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
]

const isChunkLoadError = (message: string | undefined): boolean =>
  !!message && CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message))

const handleChunkLoadError = (message: string | undefined) => {
  if (!isChunkLoadError(message)) return
  if (!canReloadNow()) return

  markReloaded()
  window.location.reload()
}

/**
 * No lazy-loaded routes exist yet (the whole route tree is statically imported), so this
 * doesn't fire in production today — but a `ChunkLoadError` is a much stronger "you're on
 * a stale build" signal than any of the other tiers, and needs to be in place before the
 * upcoming route-based code-splitting work ships. See EL-13298.
 */
export const useChunkLoadErrorReload = () => {
  useEffect(() => {
    const onError = (event: ErrorEvent) => handleChunkLoadError(event.message)
    const onRejection = (event: PromiseRejectionEvent) =>
      handleChunkLoadError(event.reason?.message ?? String(event.reason))

    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onRejection)

    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onRejection)
    }
  }, [])
}
